import { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  fetchInboxList,
  fetchAnnouncementList,
  fetchInboxSuitesList,
  fetchInboxServiceRequestList,
  fetchChatMessages,
  fetchChatUserInformation,
  fetchInboxAnnouncementsByProperty,
  postMessage,
  sendEmailNotification,
  isApiError,
} from 'src/services/client';
import { toast } from 'react-toastify';
import { ToastFail } from 'src/helpers/validationHelper';
import { upload } from 'src/helpers/upLoadHelper';
import { ErrorMessages } from 'src/common/constants';
import {
  resolveChatRecipientUserId,
  chatThreadHasNoValidRecipient,
  buildChatMessagePayload,
  buildVideoChatChannelName,
  buildVideoChatInviteData,
  ChatMessagePayload,
} from './helpers';
import {
  InboxServiceRequestListResponse,
  InboxServiceRequestRow,
} from 'src/services/api/InboxServiceRequests/apiContracts';
import {
  UseInboxUnreadCount,
  useInboxUnreadCount,
} from 'src/SharedCustomHooks/UseInboxUnreadCount/useInboxUnreadCount';

interface UseInboxAPIDataState {
  inboxList: any[];
  announcementsList: any[];
  suitesList: any[];
  serviceRequestsList: InboxServiceRequestRow[];
  chatMessagesList: any[];
  pendingChatMessageText: string | null;
  chatUserInformation: any;
  propertyAnnouncementsList: any[];
}

interface UseInboxAPILoadingState {
  loadingInboxList: boolean;
  loadingAnnouncements: boolean;
  loadingSuites: boolean;
  loadingServiceRequests: boolean;
  loadingChatMessages: boolean;
  sendingMessage: boolean;
  loadingPropertyAnnouncements: boolean;
  sendingVideoChatInvite: boolean;
}

export interface InboxFetchParams {
  search: string;
  showLoader: boolean;
}

export interface SendChatMessageParams {
  thread: any;
  messageText: string;
  attachment: File | null;
  search: string;
}

export type SendChatMessageResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'missing-home-owner' | 'upload-failed' | 'send-failed';
    };

export interface SendVideoChatInviteParams {
  thread: any;
  message: any;
}

interface PostChatMessageParams {
  messageData: any;
  isNewThread: boolean;
  search: string;
}

export interface FetchPropertyAnnouncementsParams {
  propertyId: string;
}

export interface UseInboxAPIReturn {
  inboxList: any[];
  loadingInboxList: boolean;
  announcementsList: any[];
  loadingAnnouncements: boolean;
  suitesList: any[];
  loadingSuites: boolean;
  serviceRequestsList: InboxServiceRequestRow[];
  loadingServiceRequests: boolean;
  chatMessagesList: any[];
  loadingChatMessages: boolean;
  pendingChatMessageText: string | null;
  chatUserInformation: any;
  sendingMessage: boolean;
  propertyAnnouncementsList: any[];
  loadingPropertyAnnouncements: boolean;
  sendingVideoChatInvite: boolean;
  onFetchInboxList: (params: InboxFetchParams) => Promise<any[]>;
  onFetchAnnouncementList: (params: InboxFetchParams) => Promise<void>;
  onFetchInboxSuitesList: (params: InboxFetchParams) => Promise<void>;
  onFetchInboxServiceRequestList: (params: InboxFetchParams) => Promise<void>;
  onFetchChatMessages: (
    referenceId: string,
    referenceType: string
  ) => Promise<void>;
  onFetchChatUserInformation: (
    referenceId: string,
    referenceType: string
  ) => Promise<void>;
  onSendChatMessage: (
    params: SendChatMessageParams
  ) => Promise<SendChatMessageResult>;
  onFetchAnnouncementsByProperty: (
    params: FetchPropertyAnnouncementsParams
  ) => Promise<void>;
  onSendVideoChatInvite: (
    params: SendVideoChatInviteParams
  ) => Promise<{ ok: boolean }>;
  clearChatMessages: () => void;
  clearChatUserInformation: () => void;
  clearPropertyAnnouncements: () => void;
  markInboxThreadRead: (
    referenceId: string,
    referenceType: string
  ) => Promise<void>;
  prependInboxThread: (thread: any) => void;
}

// legacy/workaround: the send-message API returns isRead as a string
// ("true"/"false") on some paths; normalize to a real boolean.
const normalizeIsReadFlag = (isRead: any): boolean => {
  if (isRead === 'true') {
    return true;
  }

  if (isRead === 'false') {
    return false;
  }

  return isRead;
};

const TOAST_OPTIONS = { autoClose: 3000, position: 'top-center' } as const;

const FIRST_PAGE = 1;

export const useInboxAPI = (): UseInboxAPIReturn => {
  const { markThreadReadAndRefresh }: UseInboxUnreadCount =
    useInboxUnreadCount();

  const [data, setData] = useState<UseInboxAPIDataState>({
    inboxList: [],
    announcementsList: [],
    suitesList: [],
    serviceRequestsList: [],
    chatMessagesList: [],
    pendingChatMessageText: null,
    chatUserInformation: {},
    propertyAnnouncementsList: [],
  });

  const [loading, setLoading] = useState<UseInboxAPILoadingState>({
    loadingInboxList: false,
    loadingAnnouncements: false,
    loadingSuites: false,
    loadingServiceRequests: false,
    loadingChatMessages: false,
    sendingMessage: false,
    loadingPropertyAnnouncements: false,
    sendingVideoChatInvite: false,
  });

  const patchData = (patch: Partial<UseInboxAPIDataState>) => {
    setData((prev: UseInboxAPIDataState) => ({ ...prev, ...patch }));
  };

  const patchLoading = (patch: Partial<UseInboxAPILoadingState>) => {
    setLoading((prev: UseInboxAPILoadingState) => ({ ...prev, ...patch }));
  };

  const user: any = useSelector(selectAuthenticatedUser);
  const userId: string = user?.id;
  const roleName: string = user?.roles?.[0]?.role?.name;

  const onFetchInboxList = async ({
    search,
    showLoader,
  }: InboxFetchParams): Promise<any[]> => {
    if (showLoader) {
      patchLoading({ loadingInboxList: true });
    }

    try {
      const response: any = await fetchInboxList(userId, roleName, search);

      if (isApiError(response)) {
        // legacy/workaround: apiReq returns { isError: true } instead of a
        // Response during stale-auth boots; suppress the toast (auth lifecycle
        // race — see serviceRequestDashboard.tsx fetchDashboard).
        console.warn(
          '[onFetchInboxList] apiReq returned isError; suppressing toast.',
          response
        );
        patchData({ inboxList: [] });
        return [];
      }

      if (!response.ok && response.status !== 200) {
        toast.error(
          '🚫 Something went wrong with fetching messages, please try again.',
          TOAST_OPTIONS
        );
        patchData({ inboxList: [] });
        return [];
      }

      const threads: any[] = (await response.json()) ?? [];

      patchData({ inboxList: threads });
      return threads;
    } catch (error) {
      console.error('Error fetching inbox list:', error);
      toast.error(
        '🚫 Something went wrong with fetching messages, please try again.',
        TOAST_OPTIONS
      );
      patchData({ inboxList: [] });
      return [];
    } finally {
      patchLoading({ loadingInboxList: false });
    }
  };

  const onFetchAnnouncementList = async ({
    search,
    showLoader,
  }: InboxFetchParams): Promise<void> => {
    if (showLoader) {
      patchLoading({ loadingAnnouncements: true });
    }

    try {
      const response: any = await fetchAnnouncementList(
        userId,
        roleName,
        search
      );

      if (isApiError(response)) {
        // legacy/workaround: same stale-auth toast suppression as onFetchInboxList.
        console.warn(
          '[onFetchAnnouncementList] apiReq returned isError; suppressing toast.',
          response
        );
        patchData({ announcementsList: [] });
        return;
      }

      if (!response.ok && response.status !== 200) {
        toast.error(
          '🚫 Something went wrong with fetching announcements, please try again.',
          TOAST_OPTIONS
        );
        patchData({ announcementsList: [] });
        return;
      }

      const responseJson: any = await response.json();

      if (responseJson?.statusCode !== 200) {
        toast.error(
          '🚫 Something went wrong with fetching announcements, please try again.',
          TOAST_OPTIONS
        );
        patchData({ announcementsList: [] });
        return;
      }

      patchData({ announcementsList: responseJson.data ?? [] });
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error(
        '🚫 Something went wrong with fetching announcements, please try again.',
        TOAST_OPTIONS
      );
      patchData({ announcementsList: [] });
    } finally {
      patchLoading({ loadingAnnouncements: false });
    }
  };

  const onFetchInboxSuitesList = async ({
    search,
    showLoader,
  }: InboxFetchParams): Promise<void> => {
    if (showLoader) {
      patchLoading({ loadingSuites: true });
    }

    try {
      const response: any = await fetchInboxSuitesList(
        userId,
        roleName,
        search
      );

      if (isApiError(response)) {
        // legacy/workaround: same stale-auth toast suppression as onFetchInboxList.
        console.warn(
          '[onFetchInboxSuitesList] apiReq returned isError; suppressing toast.',
          response
        );
        patchData({ suitesList: [] });
        return;
      }

      if (!response.ok && response.status !== 200) {
        patchData({ suitesList: [] });
        return;
      }

      const suites: any[] = (await response.json()) ?? [];

      patchData({ suitesList: suites });
    } catch (error) {
      console.error('Error fetching inbox suites:', error);
      toast.error(
        '🚫 Something went wrong with fetching suites, please try again.',
        TOAST_OPTIONS
      );
      patchData({ suitesList: [] });
    } finally {
      patchLoading({ loadingSuites: false });
    }
  };

  const fetchServiceRequestsPageData = async (
    page: number,
    search: string
  ): Promise<{ results: InboxServiceRequestRow[]; pageCount: number }> => {
    const emptyPage = { results: [], pageCount: 0 };

    try {
      const response: any = await fetchInboxServiceRequestList(
        userId,
        roleName,

        page,
        search
      );

      if (isApiError(response)) {
        // legacy/workaround: same stale-auth toast suppression as onFetchInboxList.
        console.warn(
          '[fetchServiceRequestsPageData] apiReq returned isError; suppressing toast.',
          response
        );
        return emptyPage;
      }

      if (!response.ok && response.status !== 200) {
        return emptyPage;
      }

      const responseJson: InboxServiceRequestListResponse =
        await response.json();

      return {
        results: responseJson?.serviceRequestsList?.results ?? [],
        pageCount: responseJson?.serviceRequestsList?.pageCount ?? 0,
      };
    } catch (error) {
      console.error('Error fetching inbox service requests:', error);
      toast.error(
        '🚫 Something went wrong with fetching service requests, please try again.',
        TOAST_OPTIONS
      );
      return emptyPage;
    }
  };

  const onFetchInboxServiceRequestList = async ({
    search,
    showLoader,
  }: InboxFetchParams): Promise<void> => {
    patchData({ serviceRequestsList: [] });
    patchLoading({ loadingServiceRequests: showLoader });

    const firstPage = await fetchServiceRequestsPageData(FIRST_PAGE, search);

    const remainingPageNumbers: number[] = [];

    for (let page = FIRST_PAGE + 1; page <= firstPage.pageCount; page++) {
      remainingPageNumbers.push(page);
    }

    const remainingPages = await Promise.all(
      remainingPageNumbers.map((page: number) =>
        fetchServiceRequestsPageData(page, search)
      )
    );

    const allResults: InboxServiceRequestRow[] = [
      ...firstPage.results,
      ...remainingPages.flatMap(
        (pageData: { results: InboxServiceRequestRow[] }) => pageData.results
      ),
    ];

    patchData({ serviceRequestsList: allResults });
    patchLoading({ loadingServiceRequests: false });
  };

  const onFetchChatMessages = async (
    referenceId: string,
    referenceType: string
  ): Promise<void> => {
    patchLoading({ loadingChatMessages: true });

    try {
      const response: any = await fetchChatMessages(referenceId, referenceType);

      if (isApiError(response)) {
        // legacy/workaround: same stale-auth toast suppression as onFetchInboxList.
        console.warn(
          '[onFetchChatMessages] apiReq returned isError; suppressing toast.',
          response
        );
        patchData({ chatMessagesList: [] });
        return;
      }

      if (!response.ok && response.status !== 200) {
        patchData({ chatMessagesList: [] });
        return;
      }

      const messageGroups: any[] = (await response.json()) ?? [];

      patchData({ chatMessagesList: messageGroups });
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      toast.error(
        '🚫 Something went wrong with fetching messages, please try again.',
        TOAST_OPTIONS
      );
      patchData({ chatMessagesList: [] });
    } finally {
      patchLoading({ loadingChatMessages: false });
    }
  };

  const onFetchChatUserInformation = async (
    referenceId: string,
    referenceType: string
  ): Promise<void> => {
    try {
      const response: any = await fetchChatUserInformation(
        referenceId,
        referenceType
      );

      if (isApiError(response)) {
        // legacy/workaround: same stale-auth toast suppression as onFetchInboxList.
        console.warn(
          '[onFetchChatUserInformation] apiReq returned isError; suppressing toast.',
          response
        );
        patchData({ chatUserInformation: {} });
        return;
      }

      if (!response.ok && response.status !== 200) {
        toast.error(
          '🚫 Something went wrong with fetching sender information, please try again.',
          TOAST_OPTIONS
        );
        patchData({ chatUserInformation: {} });
        return;
      }

      const senderInformation: any = (await response.json()) ?? {};

      patchData({ chatUserInformation: senderInformation });
    } catch (error) {
      console.error('Error fetching chat user information:', error);
      toast.error(
        '🚫 Something went wrong with fetching sender information, please try again.',
        TOAST_OPTIONS
      );
      patchData({ chatUserInformation: {} });
    }
  };

  const appendMessageToChatList = (message: any) => {
    const today: string = moment().format('MM/DD/YYYY');

    setData((prev: UseInboxAPIDataState) => {
      const todayGroup = prev.chatMessagesList.find(
        (group: any) => group.date === today
      );

      if (!todayGroup) {
        const newGroup = { date: today, messages: [message] };

        return {
          ...prev,
          chatMessagesList: [...prev.chatMessagesList, newGroup],
          pendingChatMessageText: null,
        };
      }

      const updatedGroups = prev.chatMessagesList.map((group: any) => {
        if (group.date !== today) {
          return group;
        }

        return { ...group, messages: [...group.messages, message] };
      });

      return {
        ...prev,
        chatMessagesList: updatedGroups,
        pendingChatMessageText: null,
      };
    });
  };

  const clearPendingChatMessage = () => {
    setData((prev: UseInboxAPIDataState) => {
      if (prev.pendingChatMessageText === null) {
        return prev;
      }

      return { ...prev, pendingChatMessageText: null };
    });
  };

  const applyMessagePreviewToThread = (message: any) => {
    setData((prev: UseInboxAPIDataState) => ({
      ...prev,
      inboxList: prev.inboxList.map((thread: any) => {
        if (thread.referenceId !== message.referenceId) {
          return thread;
        }

        return {
          ...thread,
          id: message.id,
          message: message.message,
          sentOn: message.sentOn,
          sentBy: message.sentBy,
          sentByName: message.sentByName,
          sentByImage: message.sentByImage,
          sentTo: message.sentTo,
          sentToName: message.sentToName,
          sentToImage: message.sentToImage,
          status: message.status,
          fileName: message.fileName,
          fileURL: message.fileURL,
          messageOrigin: message.messageOrigin,
          receivedOn: message.receivedOn,
          isRead: normalizeIsReadFlag(message.isRead),
        };
      }),
    }));
  };

  const isThreadAtTopOfInbox = (referenceId: string): boolean => {
    const topThread: any = data.inboxList[0];

    return topThread?.referenceId === referenceId;
  };

  const postChatMessageToAPI = async ({
    messageData,
    isNewThread,
    search,
  }: PostChatMessageParams): Promise<{ ok: boolean }> => {
    try {
      const response: any = await postMessage(messageData);

      if (isApiError(response) || (!response.ok && response.status !== 200)) {
        toast.error(
          '🚫 Something went wrong with sending message, please try again.',
          TOAST_OPTIONS
        );
        return { ok: false };
      }

      const responseJson: any = await response.json();

      if (responseJson?.statusCode !== 200) {
        toast.error('🚫 Message sending failed.', TOAST_OPTIONS);
        return { ok: false };
      }

      const sentMessage: any = responseJson.messages;

      if (sentMessage && sentMessage.id) {
        appendMessageToChatList(sentMessage);
        applyMessagePreviewToThread(sentMessage);
      }

      const needsListReorder: boolean =
        isNewThread || !isThreadAtTopOfInbox(sentMessage?.referenceId);

      if (needsListReorder) {
        await onFetchInboxList({ search, showLoader: false });
      }

      return { ok: true };
    } catch (error) {
      console.error('Error sending inbox message:', error);
      toast.error(
        '🚫 Something went wrong with sending message, please try again.',
        TOAST_OPTIONS
      );
      return { ok: false };
    }
  };

  const uploadChatAttachment = async (
    attachment: File | null
  ): Promise<{ ok: boolean; imageUrl: string | null }> => {
    if (!attachment) {
      return { ok: true, imageUrl: null };
    }

    try {
      const imageUrl: string = await upload(attachment);

      return { ok: true, imageUrl };
    } catch (error) {
      console.error('Inbox chat attachment upload failed:', error);
      toast.error(
        '🚫 Failed to upload the attachment, so your message was not sent. Please try again. If this issue persists please contact our support.',
        TOAST_OPTIONS
      );

      return { ok: false, imageUrl: null };
    }
  };

  const onSendChatMessage = async ({
    thread,
    messageText,
    attachment,
    search,
  }: SendChatMessageParams): Promise<SendChatMessageResult> => {
    const recipientUserId: string | null = resolveChatRecipientUserId({
      thread,
      chatUserInformation: data.chatUserInformation,
      currentUserId: userId,
      roleName,
    });

    if (chatThreadHasNoValidRecipient(thread, roleName, recipientUserId)) {
      toast.error(
        '🚫 There is no homeowner associated to this suite. Can not send a message now.',
        TOAST_OPTIONS
      );
      return { ok: false, reason: 'missing-home-owner' };
    }

    patchData({ pendingChatMessageText: messageText });
    patchLoading({ sendingMessage: true });

    try {
      const uploadResult: any = await uploadChatAttachment(attachment);

      if (!uploadResult.ok) {
        return { ok: false, reason: 'upload-failed' };
      }

      const messageData: ChatMessagePayload = buildChatMessagePayload({
        thread,
        messageText,
        recipientUserId,
        imageUrl: uploadResult.imageUrl,
        senderUserId: userId,
      });

      const isNewThread: boolean = Boolean(thread.isNew);

      const postResult = await postChatMessageToAPI({
        messageData,
        isNewThread,
        search,
      });

      if (!postResult.ok) {
        return { ok: false, reason: 'send-failed' };
      }

      return { ok: true };
    } finally {
      clearPendingChatMessage();
      patchLoading({ sendingMessage: false });
    }
  };

  const onFetchAnnouncementsByProperty = async ({
    propertyId,
  }: FetchPropertyAnnouncementsParams): Promise<void> => {
    patchLoading({ loadingPropertyAnnouncements: true });

    try {
      const response: any = await fetchInboxAnnouncementsByProperty(
        userId,
        roleName,
        propertyId
      );

      if (isApiError(response)) {
        // legacy/workaround: same stale-auth toast suppression as onFetchInboxList.
        console.warn(
          '[onFetchAnnouncementsByProperty] apiReq returned isError; suppressing toast.',
          response
        );
        patchData({ propertyAnnouncementsList: [] });
        return;
      }

      if (!response.ok && response.status !== 200) {
        patchData({ propertyAnnouncementsList: [] });
        return;
      }

      const announcementGroups: any[] = (await response.json()) ?? [];

      patchData({ propertyAnnouncementsList: announcementGroups });
    } catch (error) {
      console.error('Error fetching property announcements:', error);
      patchData({ propertyAnnouncementsList: [] });
    } finally {
      patchLoading({ loadingPropertyAnnouncements: false });
    }
  };

  const onSendVideoChatInvite = async ({
    thread,
    message,
  }: SendVideoChatInviteParams): Promise<{ ok: boolean }> => {
    const channelName: string = buildVideoChatChannelName(thread);
    const inviteData = buildVideoChatInviteData({
      thread,
      message,
      channelName,
    });

    patchLoading({ sendingVideoChatInvite: true });

    try {
      const response: any = await sendEmailNotification(inviteData);

      if (isApiError(response)) {
        // legacy/workaround: same stale-auth toast suppression as onFetchInboxList.
        console.warn(
          '[onSendVideoChatInvite] apiReq returned isError; suppressing toast.',
          response
        );
        return { ok: false };
      }

      if (!response.ok && response.status !== 200) {
        ToastFail(ErrorMessages.sendEmailFailed);
        return { ok: false };
      }

      const responseJson: any = await response.json();

      if (responseJson?.statusCode !== 200) {
        ToastFail(ErrorMessages.sendEmailFailed);
        return { ok: false };
      }

      return { ok: true };
    } catch (error) {
      console.error('Error sending video chat invitation:', error);
      ToastFail(ErrorMessages.sendEmailFailed);
      return { ok: false };
    } finally {
      patchLoading({ sendingVideoChatInvite: false });
    }
  };

  const clearChatMessages = () => {
    patchData({ chatMessagesList: [] });
  };

  const clearChatUserInformation = () => {
    patchData({ chatUserInformation: {} });
  };

  const clearPropertyAnnouncements = () => {
    patchData({ propertyAnnouncementsList: [] });
  };

  const applyThreadReadFlag = (referenceId: string) => {
    setData((prev: UseInboxAPIDataState) => ({
      ...prev,
      inboxList: prev.inboxList.map((thread: any) => {
        if (thread.referenceId !== referenceId) {
          return thread;
        }

        return { ...thread, isRead: true };
      }),
    }));
  };

  const markInboxThreadRead = async (
    referenceId: string,
    referenceType: string
  ): Promise<void> => {
    const { ok } = await markThreadReadAndRefresh(referenceId, referenceType);

    if (!ok) {
      return;
    }

    applyThreadReadFlag(referenceId);
  };

  const prependInboxThread = (thread: any) => {
    setData((prev: UseInboxAPIDataState) => ({
      ...prev,
      inboxList: [thread, ...prev.inboxList],
    }));
  };

  return {
    inboxList: data.inboxList,
    loadingInboxList: loading.loadingInboxList,
    announcementsList: data.announcementsList,
    loadingAnnouncements: loading.loadingAnnouncements,
    suitesList: data.suitesList,
    loadingSuites: loading.loadingSuites,
    serviceRequestsList: data.serviceRequestsList,
    loadingServiceRequests: loading.loadingServiceRequests,
    chatMessagesList: data.chatMessagesList,
    loadingChatMessages: loading.loadingChatMessages,
    pendingChatMessageText: data.pendingChatMessageText,
    chatUserInformation: data.chatUserInformation,
    sendingMessage: loading.sendingMessage,
    propertyAnnouncementsList: data.propertyAnnouncementsList,
    loadingPropertyAnnouncements: loading.loadingPropertyAnnouncements,
    sendingVideoChatInvite: loading.sendingVideoChatInvite,
    onFetchInboxList,
    onFetchAnnouncementList,
    onFetchInboxSuitesList,
    onFetchInboxServiceRequestList,
    onFetchChatMessages,
    onFetchChatUserInformation,
    onSendChatMessage,
    onFetchAnnouncementsByProperty,
    onSendVideoChatInvite,
    clearChatMessages,
    clearChatUserInformation,
    clearPropertyAnnouncements,
    markInboxThreadRead,
    prependInboxThread,
  };
};
